import type { DueTask } from "../types";
import { useIsSettling } from "../hooks/useSettlingTasks";
import { UP_FOR_GRABS_LABEL, taskDoneAriaLabel } from "../lib/copy";

export interface TaskRowProps {
  task: DueTask;
  done: boolean;
  overdue: boolean;
  onToggle: (task: DueTask) => void;
}

/** Visible text tag on every Past-due row, so overdue never rests on
 * color alone (WCAG 1.4.1). */
export const OVERDUE_TAG_LABEL = "Overdue";

/**
 * Sprint-003 A5: Today/My list are now always the signed-in person's own
 * personal list (`GET /api/v1/due` defaults to `scope=mine`), so a
 * non-null assignee on any visible row is always "me" — no badge needed
 * (design-spec-today-card.md's "name badges collapse on personal views").
 * The one exception a `mine`-scoped response can still contain is a
 * genuinely unowned cadence chore (`assignee_id: null` — visible to
 * everyone, "pickable by anyone" per `app/api/v1/due.py`'s documented
 * judgment call) — that row gets the warm "Up for grabs" badge instead of
 * the old cold "Unassigned" wording (design-qa-sprint-002.md's recommendation).
 */
function assigneeBadge(task: DueTask): string | null {
  return task.assignee_id === null ? UP_FOR_GRABS_LABEL : null;
}

/**
 * UX review (done-feedback): the WHOLE row is the tap target — one
 * `<button>` wrapping the check box, title and meta line (gloves; the old
 * 48px box was the only live spot). The row has no other controls, so
 * there is nothing to nest. Its accessible name stays the
 * `taskDoneAriaLabel` copy; the meta line (phrase, "Overdue", "Up for
 * grabs") is attached as its description.
 *
 * A just-tapped row is "settling" (`useSettlingTasks.ts`) — still in its
 * original section, rendered checked and struck through, until `useDue`
 * moves it to Done today a moment later. Tapping it again undoes.
 */
export function TaskRow({ task, done, overdue, onToggle }: TaskRowProps) {
  const settling = useIsSettling(task.id);
  const checked = done || settling;
  const metaText = overdue && task.overdue_phrase ? task.overdue_phrase : task.phrase;
  const ariaLabel = taskDoneAriaLabel(task.title, checked);
  const badge = assigneeBadge(task);
  const metaId = `task-meta-${task.id}`;

  return (
    <li
      class={`task-row${overdue ? " is-overdue" : ""}${checked ? " is-done" : ""}${
        settling && !done ? " is-settling" : ""
      }`}
      data-task-id={task.id}
      data-task-kind={task.kind}
    >
      <button
        type="button"
        class="task-row-main"
        aria-pressed={checked}
        aria-label={ariaLabel}
        aria-describedby={metaId}
        onClick={() => onToggle(task)}
      >
        <span class="check-circle" aria-hidden="true">
          {checked ? <span class="check-glyph">✓</span> : null}
        </span>
        <span class="task-copy">
          <span class="task-title">{task.title}</span>
          <span class="task-meta" id={metaId}>
            {overdue ? <span class="task-overdue-tag">{OVERDUE_TAG_LABEL}</span> : null}
            {metaText}
            {badge ? <span class="task-meta-part">{badge}</span> : null}
          </span>
        </span>
      </button>
    </li>
  );
}
